import React from 'react';
import { 
  Leaf, 
  Sparkles, 
  ShieldCheck, 
  ArrowLeft, 
  ArrowRight,
  HeartPulse,
  Award,
  MapPin
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useStore } from '../../context/StoreContext';

const HeroSection = ({ onShopClick, onTibbiClick, onConsultClick }) => {
  const { lang, t, isRtl } = useLanguage();
  const { settings, cmsContent } = useStore();

  const heroHeadline = lang === 'ur' 
    ? (cmsContent?.hero?.headlineUrdu || 'خالص جڑی بوٹیاں اور طبِ پاکستانی (قانون مفرد اعضاء) کا مرکز')
    : (cmsContent?.hero?.headlineEn || 'Pure Botanicals & Authentic Tibb-e-Pakistani (Qanoon-e-Mufrad Aza)');

  const heroSubheading = lang === 'ur'
    ? (cmsContent?.hero?.subheadingUrdu || 'ہمالین پنسار سٹور پر قانون مفرد اعضاء (حضرت صابر ملتانیؒ) کے سنہری اصولوں یعنی تحریک، تسکین اور تحلیل کے عین مطابق تیار کردہ مصفی، مقوی اور اکسیر نسخہ جات دستیاب ہیں۔ اپنے اعضائے رئیسہ اور نبض کے مطابق خالص نباتاتی علاج حاصل کریں۔')
    : (cmsContent?.hero?.subheadingEn || 'Experience natural healing guided by the scientific organ-tissue pathology of Tibb-e-Pakistani (Qanoon-e-Mufrad Aza). Authentic herbs and formulations prepared with utmost purity.');

  return (
    <div className="relative overflow-hidden bg-gradient-to-b from-pansar-900 via-pansar-950 to-pansar-900 text-white py-12 md:py-20">
      {/* Decorative herbal background patterns */}
      <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#479d4b_1px,transparent_1px)] [background-size:16px_16px] pointer-events-none" />
      <div className="absolute -top-24 -left-24 w-96 h-96 bg-emerald-600/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-amber-600/20 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          
          {/* Main Hero Copy (8 cols) */}
          <div className="lg:col-span-7 text-center lg:text-start space-y-6">
            
            {/* Top Hakim & Dawakhana Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-800/80 border border-emerald-600/50 text-amber-300 text-xs sm:text-sm font-medium shadow-inner backdrop-blur-sm">
              <Award className="w-4 h-4 text-amber-400 shrink-0" />
              <span className="font-urdu">
                {lang === 'ur' ? (settings?.dawakhanaNameUrdu || 'دواخانہ طب پاکستانی') : (settings?.dawakhanaNameEn || 'Dawakhana Tibb-e-Pakistani')} • {lang === 'ur' ? (settings?.hakimNameUrdu || 'حکیم مولانا سید عبدالوہاب شاہ شیرازی') : (settings?.hakimNameEn || 'Hakim Syed Abdul Wahab Shah Shirazi')}
              </span>
            </div>

            {/* Main Headline */}
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black text-white leading-tight font-title tracking-tight">
              {heroHeadline}
            </h1>

            {/* Subheading */}
            <p className="text-emerald-100 text-sm sm:text-lg max-w-2xl mx-auto lg:mx-0 font-urdu leading-relaxed">
              {heroSubheading}
            </p>

            {/* Action Buttons */}
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 pt-2">
              <button
                onClick={onShopClick}
                className="px-6 py-3.5 rounded-2xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-sm sm:text-base shadow-lg shadow-amber-500/25 transition-all transform hover:-translate-y-0.5 flex items-center gap-2 font-urdu"
              >
                <span>{lang === 'ur' ? (cmsContent?.hero?.btn1TextUrdu || 'تمام ادویات و مصنوعات دیکھیں') : (cmsContent?.hero?.btn1TextEn || 'Shop All Products')}</span>
                {isRtl ? <ArrowLeft className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
              </button>

              <button
                type="button"
                onClick={onConsultClick}
                className="px-5 py-3.5 rounded-2xl bg-emerald-700 hover:bg-emerald-600 text-white font-bold text-sm sm:text-base shadow-lg shadow-emerald-800/30 transition-all transform hover:-translate-y-0.5 flex items-center gap-2 font-urdu cursor-pointer"
              >
                <MapPin className="w-5 h-5 text-amber-300" />
                <span>{lang === 'ur' ? (cmsContent?.hero?.btn2TextUrdu || 'دواخانہ لوکیشن و پتہ') : (cmsContent?.hero?.btn2TextEn || 'Dawakhana Location & Address')}</span>
              </button>

              <button
                onClick={onTibbiClick}
                className="px-5 py-3.5 rounded-2xl bg-white/10 hover:bg-white/15 text-emerald-100 hover:text-white border border-white/15 font-medium text-sm transition font-urdu flex items-center gap-2"
              >
                <HeartPulse className="w-4 h-4 text-rose-400" />
                <span>{lang === 'ur' ? (cmsContent?.hero?.btn3TextUrdu || 'قانون مفرد اعضاء تشخیصی گائیڈ') : (cmsContent?.hero?.btn3TextEn || 'Qanoon Mufrad Aza Guide')}</span>
              </button>
            </div>

            {/* Quick Metrics */}
            <div className="grid grid-cols-3 gap-3 pt-4 border-t border-emerald-800/60 max-w-lg mx-auto lg:mx-0">
              <div className="text-center lg:text-start">
                <div className="text-xl sm:text-2xl font-black text-amber-400 font-sans">
                  {cmsContent?.hero?.stat1Number || '100%'}
                </div>
                <div className="text-xs text-emerald-200 font-urdu">
                  {lang === 'ur' ? (cmsContent?.hero?.stat1LabelUrdu || 'خالص قدرتی اجزاء') : (cmsContent?.hero?.stat1LabelEn || 'Pure Natural')}
                </div>
              </div>
              <div className="text-center lg:text-start">
                <div className="text-xl sm:text-2xl font-black text-amber-400 font-sans">
                  {cmsContent?.hero?.stat2Number || '5,000+'}
                </div>
                <div className="text-xs text-emerald-200 font-urdu">
                  {lang === 'ur' ? (cmsContent?.hero?.stat2LabelUrdu || 'مطمئن مریض') : (cmsContent?.hero?.stat2LabelEn || 'Happy Patients')}
                </div>
              </div>
              <div className="text-center lg:text-start">
                <div className="text-xl sm:text-2xl font-black text-amber-400 font-sans">
                  {cmsContent?.hero?.stat3Number || '24/7'}
                </div>
                <div className="text-xs text-emerald-200 font-urdu">
                  {lang === 'ur' ? (cmsContent?.hero?.stat3LabelUrdu || 'طبی رہنمائی') : (cmsContent?.hero?.stat3LabelEn || 'Consultation')}
                </div>
              </div>
            </div>

          </div>

          {/* Featured Visual Card (5 cols) */}
          <div className="lg:col-span-5 relative">
            <div className="relative mx-auto max-w-md bg-gradient-to-b from-emerald-800/60 to-emerald-950/80 rounded-3xl p-6 border border-emerald-500/30 shadow-2xl backdrop-blur-md">
              <div className="relative h-64 sm:h-72 rounded-2xl overflow-hidden shadow-inner group">
                <img
                  src={cmsContent?.hero?.heroImageUrl || "https://images.unsplash.com/photo-1540420773420-3366772f4999?w=800&auto=format&fit=crop&q=80"}
                  alt="Tibb-e-Pakistani Herbs"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 text-white text-start">
                  <div className="inline-block px-3 py-1 rounded-md bg-amber-500 text-slate-950 text-xs font-bold font-title mb-1">
                    {lang === 'ur' ? (settings?.dawakhanaNameUrdu || 'دواخانہ طب پاکستانی') : (settings?.dawakhanaNameEn || 'Dawakhana Tibb-e-Pakistani')}
                  </div>
                  <h3 className="font-black text-lg font-title text-white">
                    {lang === 'ur' ? (settings?.hakimNameUrdu || 'حکیم مولانا سید عبدالوہاب شاہ شیرازی') : (settings?.hakimNameEn || 'Hakim Syed Abdul Wahab Shah Shirazi')}
                  </h3>
                  <p className="text-xs text-emerald-200 font-urdu">
                    {lang === 'ur' 
                      ? 'فاضل طب والجراحت (FTJ) - ماہر نبض و قانون مفرد اعضاء (طبِ پاکستانی)'
                      : 'FTJ Practitioner • Specialist in Tibb-e-Pakistani (Qanoon-e-Mufrad Aza)'}
                  </p>
                </div>
              </div>

              {/* Highlights List */}
              <div className="mt-4 space-y-2 text-xs text-emerald-100 font-urdu text-start">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-amber-400 shrink-0" />
                  <span>
                    {lang === 'ur' 
                      ? 'قانون مفرد اعضاء کے رہنما اصولوں پر تیار کردہ خالص مرکبات' 
                      : 'Pure formulations crafted under organ-tissue pathology'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-amber-400 shrink-0" />
                  <span>
                    {lang === 'ur' 
                      ? 'بغیر کسی مضر اثرات (Side-Effects) کے قدرتی شفا' 
                      : 'Natural holistic healing with zero harmful side effects'}
                  </span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default HeroSection;
